/**
 * Cross-cutting vocabulary: identity, time, coordinates, area and pagination.
 *
 * This package is the single declaration of the API's shape. A TypeScript type, a runtime validator
 * and an OpenAPI component all come from the same `z.object(...)`, so there is no second place to
 * update and no way for the three to disagree. That is the entire reason the package exists:
 * ADR-005 promises a native client can be added later without the API being rebuilt, and the usual
 * way to build a typed API — validation decorators on a class — declares the same shape twice, once
 * in the type and once in the decorator, with nothing keeping them in step.
 *
 * Nothing here may import an HTTP framework, a database driver or the DOM. The contract is what
 * clients and servers agree on, so it cannot depend on either side's tooling.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------- identity and time

/**
 * A UUID. Clients never construct one (API contract §1.1) — an id is assigned by the server, and
 * a client-chosen id would be a client-chosen identity.
 */
export const Id = z.uuid().meta({
  id: "Id",
  description: "UUID assigned by the server. Clients never construct ids.",
});

/**
 * An instant, with the offset required rather than optional.
 *
 * `offset: true` is not decoration: without it, `2026-10-12T08:00:00` parses, and that value means
 * 08:00 in whatever timezone the reader happens to be in. This product's entire calendar —
 * `best_time` slots, opening hours, the forecast horizon — is anchored to Vietnam time, so a
 * timestamp that silently drops the offset is a bug that would surface as a trip planned one hour
 * off. Rejecting the naive form at the boundary is cheaper than discovering it in a plan.
 */
export const IsoDateTime = z.iso.datetime({ offset: true }).meta({
  id: "IsoDateTime",
  description: "ISO 8601 instant with offset, e.g. 2026-10-12T08:00:00+07:00.",
});

/** A calendar date with no time. Dates are not instants, and this project keeps them apart. */
export const IsoDate = z.iso.date().meta({
  id: "IsoDate",
  description: "Calendar date, YYYY-MM-DD.",
});

/**
 * A WGS84 coordinate pair.
 *
 * `lat`/`lon` and not PostGIS's `lon`/`lat`. The database stores (`lon`, `lat`) because that is
 * PostGIS's convention; this is the wire format, and `{lat, lon}` is the order every map library,
 * every geocoder response and every human reads. Converting once at the database boundary is
 * cheaper than every consumer having to know which order it is looking at.
 *
 * Separated into two named numbers rather than a two-element array: `[11.9, 108.4]` is exactly the
 * input that can be silently swapped, and a swap here moves a place to the Gulf of Thailand.
 */
export const GeoPoint = z.object({
  lat: z.number().gte(-90).lte(90),
  lon: z.number().gte(-180).lte(180),
}).meta({
  id: "GeoPoint",
  description: "WGS84 coordinates. Null coordinates anywhere in this API mean unknown, never estimated.",
});

// -------------------------------------------------------------------------------------- areas

/**
 * A user-owned area, e.g. "Đà Lạt" or "Cầu Đất".
 *
 * An area is the user's own vocabulary rather than a geographic unit. That distinction is why
 * `POST /areas` is idempotent on the folded name: `da lat` and `Đà Lạt` are the same area to the
 * person typing, and a vocabulary with both in it is a vocabulary that cannot be used to filter.
 */
export const Area = z.object({
  id: Id,
  name: z.string().min(1),
}).meta({ id: "Area", description: "A user-owned area name." });

export type Area = z.infer<typeof Area>;

export const CreateAreaRequest = z.object({
  name: z.string().min(1).max(120).describe("The user's own words. A folded duplicate returns the existing area rather than an error."),
}).meta({ id: "CreateAreaRequest", description: "POST /areas. Idempotent on the folded name." });

export const AreaList = z.object({
  areas: z.array(Area),
}).meta({ id: "AreaList", description: "GET /areas. The caller's vocabulary." });

export const AreaResponse = z.object({
  area: Area,
  /**
   * Whether this call created it. Not decoration: a client that already held the vocabulary can skip
   * a refetch when it is `false`, and an import can report "3 new areas" without diffing lists.
   */
  created: z.boolean(),
}).meta({ id: "AreaResponse", description: "POST /areas. `created: false` means the name already existed." });

// --------------------------------------------------------------------------------- pagination

/**
 * A cursor-paginated page.
 *
 * Cursors, not offsets: place and trip lists mutate while a client is scrolling them (API contract
 * §1.3), and an offset into a list that changed underneath the reader skips or repeats rows. The
 * cursor is opaque to the client for the same reason — a client that parsed it would depend on the
 * ordering key.
 *
 * `next_cursor: null` means "this is the last page", which is a known fact. It is not the
 * unknown-null of §1.2, and the two must not be conflated in a client: one ends a list, the other
 * means a value was not determined.
 */
export function pageOf<S extends z.ZodType>(item: S, id: string, description: string) {
  return z.object({
    items: z.array(item),
    next_cursor: z.string().nullable().describe("Opaque. Null means this is the last page."),
  }).meta({ id, description });
}

/** Query parameters every list endpoint accepts. */
export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20)
    .describe("Server caps this at 100. Requesting more is clamped, not rejected."),
  cursor: z.string().optional().describe("Opaque cursor from a previous page's `next_cursor`."),
}).meta({ id: "PageQuery", description: "Pagination parameters." });
